/**
 * Зеркало арифметики контракта сделки: §3 и §7 спеки.
 *
 * Эти же формулы живут в contracts/types.tolk. Расхождение здесь опаснее
 * обычного бага: витрина покажет покупателю не ту сумму, а бот напомнит о
 * платеже не в тот день — и человек уедет в дефолт, доверившись интерфейсу.
 * Поэтому целочисленное деление воспроизводится буквально, на bigint, и
 * тесты гоняют те же числа, что и тесты контракта.
 */

export const TIER_SOFT = 0;
export const TIER_STANDARD = 1;
export const TIER_HARD = 2;
export const TIER_NONE = 3;

export type RefundTier =
  | typeof TIER_SOFT
  | typeof TIER_STANDARD
  | typeof TIER_HARD
  | typeof TIER_NONE;

/** Условия сделки в том виде, в каком они лежат в контракте. */
export interface DealTerms {
  totalPrice: bigint;
  upfront: bigint;
  /** Длина периода в секундах. */
  period: number;
  nPayments: number;
  refundTier: RefundTier;
  maxDeferrals: number;
  deferFee: bigint;
  feeBps: number;
  /** Unix-время, после которого сделка не принимает платежи. */
  deadline: number;
}

/** Доля возврата покупателю при дефолте, в процентах от послепредоплатных платежей. */
export function refundPercentOf(tier: RefundTier): bigint {
  if (tier === TIER_SOFT) return 75n;
  if (tier === TIER_STANDARD) return 50n;
  if (tier === TIER_HARD) return 25n;
  return 0n;
}

/** Допуск на опоздание. Не настройка продавца, а константа уровня — см. §2. */
export function graceOf(tier: RefundTier): number {
  if (tier === TIER_SOFT) return 48 * 3600;
  if (tier === TIER_STANDARD) return 96 * 3600;
  if (tier === TIER_HARD) return 144 * 3600;
  // На нулевом уровне буфер всегда пуст: продавец получает каждый платёж
  // целиком и при срыве оставляет себе всё вместе с NFT. Дефолт для него
  // выгоднее закрытия, поэтому воздуха покупателю здесь нужно больше всего.
  return 192 * 3600;
}

/**
 * Сколько плановых периодов прошло с начала. Округление вниз здесь и есть
 * смысл графика: ступенька поднимается в момент наступления срока платежа.
 */
export function elapsedPeriods(terms: DealTerms, start: number, t: number): number {
  if (t <= start) return 0;
  return Math.floor((t - start) / terms.period);
}

/**
 * required(t) = upfront + floor((t − start) / period) × amount
 *
 * На последней ступени возвращает ровно totalPrice, иначе остаток от
 * целочисленного деления завис бы вечной недоплатой в пару единиц.
 */
export function requiredAt(terms: DealTerms, start: number, t: number): bigint {
  const k = elapsedPeriods(terms, start, t);
  if (k >= terms.nPayments) return terms.totalPrice;
  // Умножаем до деления — как в контракте.
  return terms.upfront + ((terms.totalPrice - terms.upfront) * BigInt(k)) / BigInt(terms.nPayments);
}

/** Буфер, который контракт обязан удерживать: ровно обязательство по возврату. §7 */
export function retainFor(terms: DealTerms, paidTotal: bigint): bigint {
  if (paidTotal <= terms.upfront) return 0n;
  return ((paidTotal - terms.upfront) * refundPercentOf(terms.refundTier)) / 100n;
}

/** Просрочка: одно сравнение, как в контракте. */
export function isInDefault(
  terms: DealTerms,
  start: number,
  paidTotal: bigint,
  now: number,
): boolean {
  return paidTotal < requiredAt(terms, start, now - graceOf(terms.refundTier));
}

/**
 * Первая секунда, в которую сделка уже просрочена, если покупатель больше
 * ничего не внесёт.
 *
 * Граница включающая: в момент `срок + допуск` проверка контракта смотрит на
 * `t − grace`, то есть ровно на срок платежа, где ступенька уже поднялась.
 * Поэтому здесь дефолт уже наступил — показывать это время пользователю как
 * «заплати до» нельзя, для этого есть payBefore().
 *
 * Возвращает null, если внесённого хватает до самого конца графика.
 */
export function defaultsAt(terms: DealTerms, start: number, paidTotal: bigint): number | null {
  if (paidTotal >= terms.totalPrice) return null;

  // Первая ступень, которую внесённое уже не покрывает.
  for (let k = 0; k <= terms.nPayments; k += 1) {
    const dueAt = start + k * terms.period;
    if (paidTotal < requiredAt(terms, start, dueAt)) {
      return dueAt + graceOf(terms.refundTier);
    }
  }
  return null;
}

/**
 * Последняя секунда, когда платёж ещё спасает сделку. Именно это число идёт
 * в интерфейс и в напоминание бота: ошибка на единицу здесь означает, что
 * человек заплатит вовремя по нашей подсказке и всё равно потеряет деньги.
 */
export function payBefore(terms: DealTerms, start: number, paidTotal: bigint): number | null {
  const at = defaultsAt(terms, start, paidTotal);
  return at === null ? null : at - 1;
}

/** Сколько нужно доплатить, чтобы дожить до следующей ступени. */
export function amountDueBy(terms: DealTerms, start: number, paidTotal: bigint, t: number): bigint {
  const required = requiredAt(terms, start, t);
  return required > paidTotal ? required - paidTotal : 0n;
}

/**
 * Что покупатель потеряет, если бросит платить прямо сейчас.
 *
 * §2 требует показывать это суммой, а не процентом: «вернётся 25%» человек
 * не считает в уме, а «потеряешь 640 USDT» понимает сразу. На этом держится
 * легитимность жёстких уровней, так что цифра обязана быть точной.
 */
export function forfeitIfAbandoned(terms: DealTerms, paidTotal: bigint): bigint {
  return paidTotal - retainFor(terms, paidTotal);
}

/** Таблица потерь по шагам графика — то, что показывается до подтверждения сделки. */
export interface ForfeitRow {
  /** Номер платежа: 0 — предоплата. */
  step: number;
  paidAfterStep: bigint;
  refunded: bigint;
  lost: bigint;
}

export function forfeitTable(terms: DealTerms): ForfeitRow[] {
  const rows: ForfeitRow[] = [];
  for (let step = 0; step <= terms.nPayments; step += 1) {
    const paid = requiredAt(terms, 0, step * terms.period);
    const refunded = retainFor(terms, paid);
    rows.push({ step, paidAfterStep: paid, refunded, lost: paid - refunded });
  }
  return rows;
}

/**
 * Комиссия протокола с одного поступления. Удерживается с каждого платежа,
 * поэтому суммарно это Σ⌊платёж × ставка⌋ — всегда не больше ⌊цена × ставка⌋.
 */
export function feeOn(terms: DealTerms, amount: bigint): bigint {
  return (amount * BigInt(terms.feeBps)) / 10000n;
}

/** Сколько уйдёт продавцу немедленно при таком поступлении. §7 */
export function forwardOn(terms: DealTerms, paidBefore: bigint, amount: bigint): bigint {
  const retainDelta = retainFor(terms, paidBefore + amount) - retainFor(terms, paidBefore);
  return amount - feeOn(terms, amount) - retainDelta;
}
